import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useAppCtx } from "../contexts/AppContext";
import { PeriodFilter, periodParams } from "../components/PeriodFilter";
import { toast } from "sonner";
import { Trash2, Pencil, ArrowLeftRight } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

function num(n, d = 2) {
    if (n === null || n === undefined || n === "") return "—";
    const v = Number(n);
    return v.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

const blank = () => ({
    voucher_number: "",
    date: today(),
    segment: "Voice",
    from_bank_account_id: "",
    to_bank_account_id: "",
    from_amount: "",
    to_amount: "",
    fx_rate: "",
    bank_date_from: "",
    bank_date_to: "",
    remarks: "",
});

export default function ContrasPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("contras", "write");
    const { entities, currencies, selectedEntity, selectedSegment } = useAppCtx();

    const [banks, setBanks] = useState([]);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(blank());
    const [period, setPeriod] = useState({ from: null, to: null, preset: "this_month" });
    const [fxSuggesting, setFxSuggesting] = useState(false);

    const load = async () => {
        const params = { ...periodParams(period) };
        if (selectedEntity && selectedEntity !== "all") params.entity_id = selectedEntity;
        if (selectedSegment && selectedSegment !== "all") params.segment = selectedSegment;
        const { data } = await api.get("/contras", { params });
        setRows(data || []);
    };
    const loadBanks = async () => {
        const { data } = await api.get("/banks");
        setBanks(data || []);
    };

    useEffect(() => { loadBanks(); }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedEntity, selectedSegment, period]);

    const fromBank = useMemo(() => banks.find((b) => b.id === form.from_bank_account_id), [banks, form.from_bank_account_id]);
    const toBank = useMemo(() => banks.find((b) => b.id === form.to_bank_account_id), [banks, form.to_bank_account_id]);
    const sameCurrency = fromBank && toBank && fromBank.currency === toBank.currency;

    // Auto-suggest FX rate via FX master when from/to bank + amount are known
    // and currencies differ. User can override either to_amount OR fx_rate;
    // the other auto-updates. Pure UX helper — backend always re-derives
    // amount_base on save so this is purely a hint.
    const fetchFxSuggestion = async () => {
        if (!fromBank || !toBank || sameCurrency) return;
        try {
            setFxSuggesting(true);
            const fromCur = currencies.find((c) => (c.code || c) === fromBank.currency);
            const toCur = currencies.find((c) => (c.code || c) === toBank.currency);
            const fromRate = fromCur?.rate_to_usd || 1;
            const toRate = toCur?.rate_to_usd || 1;
            if (fromRate && toRate) {
                // 1 from_currency = fromRate USD; 1 to_currency = toRate USD.
                // → 1 from = (fromRate / toRate) to_currency
                const fx = (fromRate / toRate);
                setForm((f) => ({ ...f, fx_rate: fx.toFixed(6) }));
            }
        } finally {
            setFxSuggesting(false);
        }
    };

    useEffect(() => {
        // Whenever from/to bank changes & currencies differ → suggest FX once
        if (fromBank && toBank && !sameCurrency && !form.fx_rate) {
            fetchFxSuggestion();
        }
        // eslint-disable-next-line
    }, [form.from_bank_account_id, form.to_bank_account_id]);

    // Recompute to_amount from from_amount × fx_rate (cross-currency case).
    useEffect(() => {
        if (sameCurrency) {
            if (form.from_amount && form.to_amount !== form.from_amount) {
                setForm((f) => ({ ...f, to_amount: f.from_amount }));
            }
            return;
        }
        const fa = parseFloat(form.from_amount);
        const fx = parseFloat(form.fx_rate);
        if (!isNaN(fa) && !isNaN(fx) && fa > 0 && fx > 0) {
            const computed = (fa * fx).toFixed(4);
            // Only auto-update if user hasn't manually set a different to_amount
            // (small heuristic: if to_amount is empty OR equals previous auto value)
            setForm((f) => ({ ...f, to_amount: computed }));
        }
        // eslint-disable-next-line
    }, [form.from_amount, form.fx_rate, sameCurrency]);

    const openCreate = () => {
        setForm(blank());
        setEditId(null);
        setOpen(true);
    };

    const openEdit = (r) => {
        setEditId(r.id);
        setForm({
            voucher_number: r.voucher_number || "",
            date: r.date || today(),
            segment: r.segment || "Voice",
            from_bank_account_id: r.from_bank_account_id || "",
            to_bank_account_id: r.to_bank_account_id || "",
            from_amount: String(r.from_amount ?? ""),
            to_amount: String(r.to_amount ?? ""),
            fx_rate: r.fx_rate ? String(r.fx_rate) : "",
            bank_date_from: r.bank_date_from || "",
            bank_date_to: r.bank_date_to || "",
            remarks: r.remarks || "",
        });
        setOpen(true);
    };

    const submit = async () => {
        if (!form.from_bank_account_id || !form.to_bank_account_id) return toast.error("Both source and destination banks are required");
        if (form.from_bank_account_id === form.to_bank_account_id) return toast.error("Source and destination must differ");
        const fromAmt = parseFloat(form.from_amount);
        if (!fromAmt || fromAmt <= 0) return toast.error("From amount must be positive");
        const fb = banks.find((b) => b.id === form.from_bank_account_id);
        const tb = banks.find((b) => b.id === form.to_bank_account_id);
        const body = {
            voucher_number: form.voucher_number || undefined,
            date: form.date,
            segment: form.segment,
            from_entity_id: fb?.entity_id,
            to_entity_id: tb?.entity_id,
            from_bank_account_id: form.from_bank_account_id,
            to_bank_account_id: form.to_bank_account_id,
            from_amount: fromAmt,
            to_amount: form.to_amount ? parseFloat(form.to_amount) : undefined,
            fx_rate: form.fx_rate ? parseFloat(form.fx_rate) : undefined,
            bank_date_from: form.bank_date_from || undefined,
            bank_date_to: form.bank_date_to || undefined,
            remarks: form.remarks || "",
        };
        try {
            if (editId) {
                await api.put(`/contras/${editId}`, body);
                toast.success("Contra voucher updated");
            } else {
                await api.post("/contras", body);
                toast.success("Contra voucher created");
            }
            setOpen(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const remove = async (r) => {
        if (!window.confirm(`Delete contra voucher ${r.voucher_number}?`)) return;
        try {
            await api.delete(`/contras/${r.id}`);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const entityName = (id) => entities.find((e) => e.id === id)?.name || "—";
    const bankLabel = (id) => {
        const b = banks.find((x) => x.id === id);
        if (!b) return "—";
        const ent = entities.find((e) => e.id === b.entity_id);
        return `${b.name}${b.account_number ? ` · ${b.account_number}` : ""}${ent ? ` (${ent.name})` : ""}`;
    };

    return (
        <div className="space-y-5">
            <PageHeader
                eyebrow="Transactions"
                title="Contra Vouchers"
                description="Bank-to-bank fund transfers, including cross-currency. Each leg posts to its respective bank ledger in that bank's own currency."
                action={
                    canWrite && (
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button onClick={openCreate} className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="contra-create-btn">
                                    <ArrowLeftRight size={14} className="mr-1.5"/>New Contra
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm max-w-3xl" data-testid="contra-dialog">
                                <DialogHeader>
                                    <DialogTitle>{editId ? "Edit Contra Voucher" : "New Contra Voucher"}</DialogTitle>
                                </DialogHeader>
                                <div className="grid grid-cols-3 gap-3">
                                    <Field label="Date">
                                        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm font-mono h-9" data-testid="contra-date"/>
                                    </Field>
                                    <Field label="Segment">
                                        <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                                            <SelectTrigger className="rounded-sm h-9" data-testid="contra-segment"><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Voice">Voice (USD base)</SelectItem>
                                                <SelectItem value="SMS">SMS (EUR base)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Voucher # (auto if blank)">
                                        <Input value={form.voucher_number} onChange={(e) => setForm({ ...form, voucher_number: e.target.value })} className="rounded-sm font-mono h-9" placeholder="CTR-202605-0001"/>
                                    </Field>
                                </div>

                                <div className="mt-2 border border-slate-200 rounded-sm p-3 bg-slate-50">
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">From (Source bank — debited)</div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label="Source bank">
                                            <Select value={form.from_bank_account_id} onValueChange={(v) => setForm({ ...form, from_bank_account_id: v, fx_rate: "" })}>
                                                <SelectTrigger className="rounded-sm h-9" data-testid="contra-from-bank"><SelectValue placeholder="Select source bank"/></SelectTrigger>
                                                <SelectContent className="max-h-72">
                                                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{bankLabel(b.id)} — {b.currency}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label={`Amount${fromBank ? ` (${fromBank.currency})` : ""}`}>
                                            <Input type="number" step="0.0001" value={form.from_amount} onChange={(e) => setForm({ ...form, from_amount: e.target.value })} className="rounded-sm font-mono h-9 text-right" placeholder="0.00" data-testid="contra-from-amount"/>
                                        </Field>
                                        <Field label="Bank value-date — debit (optional)">
                                            <Input type="date" value={form.bank_date_from} onChange={(e) => setForm({ ...form, bank_date_from: e.target.value })} className="rounded-sm font-mono h-9"/>
                                        </Field>
                                    </div>
                                </div>

                                <div className="mt-2 border border-slate-200 rounded-sm p-3 bg-slate-50">
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">To (Destination bank — credited)</div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label="Destination bank">
                                            <Select value={form.to_bank_account_id} onValueChange={(v) => setForm({ ...form, to_bank_account_id: v, fx_rate: "" })}>
                                                <SelectTrigger className="rounded-sm h-9" data-testid="contra-to-bank"><SelectValue placeholder="Select destination bank"/></SelectTrigger>
                                                <SelectContent className="max-h-72">
                                                    {banks.filter((b) => b.id !== form.from_bank_account_id).map((b) => <SelectItem key={b.id} value={b.id}>{bankLabel(b.id)} — {b.currency}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label={`Amount${toBank ? ` (${toBank.currency})` : ""}${sameCurrency ? " — matches source" : ""}`}>
                                            <Input type="number" step="0.0001" value={form.to_amount} onChange={(e) => setForm({ ...form, to_amount: e.target.value })} className="rounded-sm font-mono h-9 text-right" placeholder="0.00" disabled={sameCurrency} data-testid="contra-to-amount"/>
                                        </Field>
                                        <Field label="Bank value-date — credit (optional)">
                                            <Input type="date" value={form.bank_date_to} onChange={(e) => setForm({ ...form, bank_date_to: e.target.value })} className="rounded-sm font-mono h-9"/>
                                        </Field>
                                    </div>
                                    {!sameCurrency && fromBank && toBank && (
                                        <div className="grid grid-cols-3 gap-3 mt-2">
                                            <Field label={`FX rate (1 ${fromBank.currency} = X ${toBank.currency})`}>
                                                <Input type="number" step="0.000001" value={form.fx_rate} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })} className="rounded-sm font-mono h-9 text-right" placeholder="0.000000" data-testid="contra-fx"/>
                                            </Field>
                                            <div className="col-span-2 self-end text-[11px] text-slate-500">
                                                {fxSuggesting ? "Looking up FX master…" : "FX defaults to the current FX-master cross rate. Override either the rate or the destination amount — the other will recompute."}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <Field label="Remarks (optional)">
                                    <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="rounded-sm h-9" placeholder="e.g. Inter-company funding for May payroll"/>
                                </Field>

                                <DialogFooter>
                                    <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                                    <Button onClick={submit} className="rounded-sm bg-[#002FA7]" data-testid="contra-submit">{editId ? "Save" : "Create"}</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )
                }
            />

            <div className="flex items-center justify-between">
                <PeriodFilter period={period} onChange={setPeriod}/>
                <span className="text-[11px] text-slate-500">{rows.length} voucher{rows.length === 1 ? "" : "s"}</span>
            </div>

            <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-[13px] financial min-w-[1200px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-3 py-2.5">Date</th>
                            <th className="px-3 py-2.5">Voucher #</th>
                            <th className="px-3 py-2.5">Seg</th>
                            <th className="px-3 py-2.5">From Bank</th>
                            <th className="px-3 py-2.5 num">From Amt</th>
                            <th className="px-3 py-2.5">Cur</th>
                            <th className="px-3 py-2.5">To Bank</th>
                            <th className="px-3 py-2.5 num">To Amt</th>
                            <th className="px-3 py-2.5">Cur</th>
                            <th className="px-3 py-2.5 num">FX</th>
                            <th className="px-3 py-2.5">Remarks</th>
                            {canWrite && <th className="px-3 py-2.5 w-24"></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={canWrite ? 12 : 11} className="text-center text-slate-500 py-10">No contra vouchers in this period</td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-slate-100" data-testid={`contra-row-${r.id}`}>
                                <td className="px-3 py-1.5 font-mono">{r.date}</td>
                                <td className="px-3 py-1.5 font-mono">{r.voucher_number}</td>
                                <td className="px-3 py-1.5">{r.segment}</td>
                                <td className="px-3 py-1.5">{r.from_bank_name} <span className="text-slate-400 text-[11px]">({entityName(r.from_entity_id)})</span></td>
                                <td className="px-3 py-1.5 text-right font-mono">{num(r.from_amount)}</td>
                                <td className="px-3 py-1.5 font-mono text-slate-500">{r.from_currency}</td>
                                <td className="px-3 py-1.5">{r.to_bank_name} <span className="text-slate-400 text-[11px]">({entityName(r.to_entity_id)})</span></td>
                                <td className="px-3 py-1.5 text-right font-mono">{num(r.to_amount)}</td>
                                <td className="px-3 py-1.5 font-mono text-slate-500">{r.to_currency}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-slate-500">{r.fx_rate === 1 ? "—" : num(r.fx_rate, 6)}</td>
                                <td className="px-3 py-1.5 text-slate-600 truncate max-w-[220px]" title={r.remarks}>{r.remarks}</td>
                                {canWrite && (
                                    <td className="px-3 py-1.5">
                                        <div className="flex items-center gap-1">
                                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(r)} data-testid={`contra-edit-${r.id}`}><Pencil size={13}/></Button>
                                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700" onClick={() => remove(r)} data-testid={`contra-delete-${r.id}`}><Trash2 size={13}/></Button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
